import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  QueryConstraint,
  DocumentData,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/config/firebase';

export const firestoreService = {
  // Generic CRUD operations
  async getDocument<T>(collectionName: string, docId: string): Promise<T | null> {
    try {
      const docRef = doc(db, collectionName, docId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as T;
      }
      return null;
    } catch (error: any) {
      console.error(`Error getting document ${docId} from ${collectionName}:`, error);
      // Return null for missing documents, throw for actual errors
      if (error?.code === 'permission-denied') {
        throw new Error('Permission denied. Please check your authentication.');
      }
      if (error?.code === 'not-found') {
        return null;
      }
      throw error;
    }
  },

  async getDocuments<T>(
    collectionName: string,
    constraints: QueryConstraint[] = []
  ): Promise<T[]> {
    try {
      const q = query(collection(db, collectionName), ...constraints);
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as T[];
    } catch (error: any) {
      console.error(`Error getting documents from ${collectionName}:`, error);
      
      // Handle specific Firestore errors
      if (error?.code === 'permission-denied') {
        throw new Error('Permission denied. Please check your authentication.');
      }
      
      // Handle missing index error
      if (error?.code === 'failed-precondition' || error?.message?.includes('index')) {
        // Extract index URL from error message if available
        const indexUrlMatch = error?.message?.match(/https:\/\/console\.firebase\.google\.com[^\s\)]+/);
        const indexUrl = indexUrlMatch ? indexUrlMatch[0] : null;
        
        const errorMessage = indexUrl 
          ? `Missing Firestore index for ${collectionName}. Create it here: ${indexUrl}`
          : `Missing Firestore index for ${collectionName}. Check Firebase Console → Firestore → Indexes.`;
        
        console.error(errorMessage);
        const enhancedError = new Error(errorMessage);
        (enhancedError as any).indexUrl = indexUrl;
        throw enhancedError;
      }
      
      // Handle not found (404) - return empty array instead of throwing
      if (error?.code === 'not-found' || error?.code === 404) {
        console.warn(`Collection ${collectionName} not found or empty`);
        return [];
      }
      
      throw error;
    }
  },

  async createDocument<T extends DocumentData>(
    collectionName: string,
    data: T,
    docId?: string
  ): Promise<string> {
    try {
      if (docId) {
        await setDoc(doc(db, collectionName, docId), {
          ...data,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
        return docId;
      } else {
        const docRef = doc(collection(db, collectionName));
        await setDoc(docRef, {
          ...data,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
        return docRef.id;
      }
    } catch (error: any) {
      console.error(`Error creating document in ${collectionName}:`, error);
      if (error?.code === 'permission-denied') {
        throw new Error('Permission denied. Please check your authentication.');
      }
      throw error;
    }
  },

  async updateDocument<T extends DocumentData>(
    collectionName: string,
    docId: string,
    data: Partial<T>
  ): Promise<void> {
    try {
      const docRef = doc(db, collectionName, docId);
      await updateDoc(docRef, {
        ...data,
        updatedAt: Timestamp.now(),
      });
    } catch (error: any) {
      console.error(`Error updating document ${docId} in ${collectionName}:`, error);
      if (error?.code === 'permission-denied') {
        throw new Error('Permission denied. Please check your authentication.');
      }
      if (error?.code === 'not-found') {
        throw new Error('Document not found.');
      }
      throw error;
    }
  },

  async deleteDocument(collectionName: string, docId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, collectionName, docId));
    } catch (error: any) {
      console.error(`Error deleting document ${docId} from ${collectionName}:`, error);
      if (error?.code === 'permission-denied') {
        throw new Error('Permission denied. Please check your authentication.');
      }
      throw error;
    }
  },

  // Helper functions for common queries
  where,
  orderBy,
  limit,
  
  // Re-export Timestamp for convenience
  Timestamp,
};


